import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { CalendarIcon, MapPinIcon, BanknoteIcon, AlertCircleIcon, MoreVerticalIcon } from 'lucide-react';
import { formatNumberWithCommasPakistani } from '../../../utils/functions';
import { PRIORITY_META, getLocationLabel, getInitials, isOverdue } from '../utils';

const KanbanCard = ({ workOrder, index, onClick, onQuickView, canViewCosts, canMove }) => {
    const priorityMeta = PRIORITY_META[workOrder.priority] || {};
    const overdue = isOverdue(workOrder);

    return (
        <Draggable draggableId={workOrder._id} index={index} isDragDisabled={!canMove}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={onClick}
                    className={`mb-3 rounded-xl border bg-white p-4 transition-all duration-200 ${
                        snapshot.isDragging
                            ? 'border-inv-brand shadow-lg rotate-[2deg] z-50'
                            : 'border-inv-cream-200 shadow-sm hover:border-inv-brand/50 hover:shadow-md'
                    }`}
                    style={{
                        ...provided.draggableProps.style,
                        // @hello-pangea/dnd injects its own stylesheet that sets the drag
                        // handle's cursor to grab/grabbing at all times; an inline style wins
                        // the specificity fight so it reverts to a plain pointer once dropped
                        // instead of staying stuck on "grab" from then on.
                        cursor: canMove ? (snapshot.isDragging ? 'grabbing' : 'pointer') : 'pointer',
                    }}
                >
                    <div className="mb-2.5 flex items-start justify-between gap-2">
                        <h4 className="text-[15px] font-semibold leading-tight text-inv-ink line-clamp-2">
                            {workOrder.title}
                        </h4>
                        <button
                            onClick={(e) => { e.stopPropagation(); onQuickView(workOrder); }}
                            className="-mr-1 -mt-1 shrink-0 rounded-lg p-1 text-[#8C8175] hover:bg-inv-cream-100 hover:text-inv-ink"
                            aria-label="Quick view"
                        >
                            <MoreVerticalIcon size={16} />
                        </button>
                    </div>

                    <div className="mb-3 flex items-center gap-1.5 text-xs text-[#8C8175]">
                        <MapPinIcon size={14} className="shrink-0" />
                        <span className="truncate">{getLocationLabel(workOrder)}</span>
                    </div>

                    <div className="mb-3 flex items-center gap-2">
                        <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${priorityMeta.badgeClass || 'bg-gray-50 text-gray-600'}`}>
                            {priorityMeta.label || workOrder.priority}
                        </span>

                        {canViewCosts && (
                            <div className="flex items-center gap-1 text-[11px] font-medium text-[#6B675F]">
                                <BanknoteIcon size={12} />
                                {workOrder.actualCost != null ? (
                                    <span><span className="text-[#8C8175]">Actual:</span> {formatNumberWithCommasPakistani(workOrder.actualCost)} PKR</span>
                                ) : workOrder.estimate != null ? (
                                    <span>{formatNumberWithCommasPakistani(workOrder.estimate)} PKR</span>
                                ) : (
                                    <span className="text-[#8C8175]">No estimate</span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between border-t border-inv-cream-200 pt-3">
                        <div className={`flex items-center gap-1.5 text-xs font-medium ${overdue ? 'text-red-600' : 'text-[#8C8175]'}`}>
                            {overdue ? <AlertCircleIcon size={14} /> : <CalendarIcon size={14} />}
                            {new Date(workOrder.dueDate).toLocaleDateString()}
                        </div>

                        <div
                            className="flex h-6 w-6 items-center justify-center rounded-full bg-inv-brand-light text-[10px] font-bold text-inv-brand shadow-sm"
                            title={workOrder.assignee?.name || 'Unassigned'}
                        >
                            {getInitials(workOrder.assignee?.name)}
                        </div>
                    </div>
                </div>
            )}
        </Draggable>
    );
};

export default KanbanCard;
